import { createSlice } from '@reduxjs/toolkit';
import { persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import {
  fetchAdminOptions,
  fetchStaffAdmins,
  fetchStaffAdminDetail,
  updateStaffAdminPlan,
} from './adminsThunks';

/**
 * The admins of the platform, as only the internal team sees them.
 *
 * Every screen behind this slice is staff's: the table, the detail and the
 * switches of each plan. The options catalog is the exception — it also feeds
 * the operator filter and the seller form, which are staff screens too, but
 * live in the admin workspace.
 */
const initialState = {
  admins: [],
  totalCount: 0,
  loading: false,
  error: null,
  filters: {
    search: '',
    page: 1,
    rowsPerPage: 20,
  },

  /** The admin open in the detail. */
  detail: null,
  loadingDetail: false,
  detailError: null,

  /** Whole roster as `{ value, label }`, for selects rather than the table. */
  options: [],
  optionsLoaded: false,
};

const adminsSlice = createSlice({
  name: 'admins',
  initialState,
  reducers: {
    setSearchFilter: (state, action) => {
      state.filters.search = action.payload;
      state.filters.page = 1;
    },
    setPage: (state, action) => {
      state.filters.page = action.payload;
    },
    setRowsPerPage: (state, action) => {
      state.filters.rowsPerPage = action.payload;
      state.filters.page = 1;
    },
    resetAdminDetail: (state) => {
      state.detail = null;
      state.detailError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchStaffAdmins.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStaffAdmins.fulfilled, (state, action) => {
        state.loading = false;
        state.admins = action.payload.results || [];
        state.totalCount = action.payload.count || 0;
      })
      .addCase(fetchStaffAdmins.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchStaffAdminDetail.pending, (state) => {
        state.loadingDetail = true;
        state.detailError = null;
      })
      .addCase(fetchStaffAdminDetail.fulfilled, (state, action) => {
        state.loadingDetail = false;
        state.detail = action.payload;
      })
      .addCase(fetchStaffAdminDetail.rejected, (state, action) => {
        state.loadingDetail = false;
        state.detailError = action.payload;
      })

      // Which switch is waiting comes from `selectIsRunning`; what to say about
      // it is a toast, see `adminsFeedback`.
      .addCase(updateStaffAdminPlan.fulfilled, (state, action) => {
        // The switch renders what came back, not what was clicked.
        if (state.detail) state.detail.admin_plan = action.payload.adminPlan;
      })

      .addCase(fetchAdminOptions.fulfilled, (state, action) => {
        state.options = action.payload;
        state.optionsLoaded = true;
      });
  },
});

export const {
  setSearchFilter,
  setPage,
  setRowsPerPage,
  resetAdminDetail,
} = adminsSlice.actions;

/** `{ [adminId]: label }`, for the listings that only carry the owner's id. */
export const selectAdminLabelById = (state) =>
  state.admins.options.reduce((byId, option) => {
    byId[option.value] = option.label;
    return byId;
  }, {});

export const adminsReducer = adminsSlice.reducer;

const persistConfig = {
  key: 'adminsFilter',
  storage,
  whitelist: ['filters'],
};

export default persistReducer(persistConfig, adminsSlice.reducer);
